import { useState } from 'react';
import { Button, Group, Paper, Text } from '@mantine/core';
import { FiCheck } from 'react-icons/fi';
import { t } from 'i18next';
import { EntryType } from '@morph-mapper/types';
import { useStore, type SuggestedChange } from '@/store';
import { resolveChange, type ResolvedChange } from './change-cards';
import { applyChange, undoChange, type AppliedChange } from './apply-change';
import { blocksFor, graphLines, problemNames, type Blocks } from './graph-change';
import { ChangePreview } from './change-preview';
import { useAppliedChanges } from './applied-changes';
import type { Graph } from '../../../graph-editor/types';

const shown = (value: unknown) =>
  value === undefined || value === null || value === ''
    ? t('chat.change.notSet')
    : String(value);

/**
 * What the field had before, when it keeps its type. A switch to another type
 * is named in its own line instead.
 */
const beforeOf = (resolved: ResolvedChange, applied: AppliedChange | null) => {
  if (applied) {
    return applied.previousType === resolved.targetType ? shown(applied.previous) : undefined;
  }
  return resolved.problem || resolved.switchesFrom ? undefined : shown(resolved.current);
};

type ChangeValueProps = {
  change: SuggestedChange;
  resolved: ResolvedChange;
  applied: AppliedChange | null;
  blocks: Blocks;
  currentGraph?: Graph;
};

/** What the change gives the field: a graph node by node, or a column or value after what it had. */
const ChangeValue = ({ change, resolved, applied, blocks, currentGraph }: ChangeValueProps) => {
  if (change.kind === 'graph') {
    return (
      <>
        <Text fz="xs">{t('chat.change.graph')}:</Text>
        {graphLines(change.value, blocks, t, currentGraph).map((line, index) => (
          <Text key={index} fz="xs" pl={8 + line.depth * 12}>
            {line.text}
          </Text>
        ))}
      </>
    );
  }
  const before = beforeOf(resolved, applied);
  return (
    <Text fz="xs">
      {t(`chat.change.${change.kind}`)}: {before !== undefined ? `${before} → ` : ''}
      {String(change.value)}
    </Text>
  );
};

/** Why a change doesn't fit the field; for a graph, by the node and option it is about. */
const problemText = (change: SuggestedChange, { problem, graphProblem }: ResolvedChange, blocks: Blocks) =>
  t(
    `chat.change.problem.${problem}`,
    change.kind === 'graph' && graphProblem
      ? problemNames(change.value, graphProblem, blocks, t)
      : undefined
  );

/**
 * A change the mapping assistant suggests, shown under its answer: the field,
 * what it has now and what it would get. When the change fits the field it can
 * be applied, and undone again; when it doesn't, the card says why.
 */
export const ChangeCard = ({ change, changeKey }: { change: SuggestedChange; changeKey: string }) => {
  const entries = useStore(({ entries: e }) => e.entries);
  const suggestions = useStore(({ ai }) => ai.suggestions);
  // Not set before a template is loaded, while the chat is already open.
  const variant = useStore(({ config: c }) => c.templateVariant);
  const blocks = blocksFor(variant);
  // Kept outside the card, which closes with the chat.
  const applied = useAppliedChanges((s) => s.applied[changeKey] ?? null);
  const setApplied = (next: AppliedChange | null) =>
    useAppliedChanges.getState().setApplied(changeKey, next);
  const [failed, setFailed] = useState(false);
  const resolved = resolveChange(change, entries, suggestions, blocks);
  const { entryId, problem, switchesFrom, targetType } = resolved;
  // The field's graph now: a graph change may keep some of its nodes.
  const currentGraph = useStore(({ graphs: g }) => {
    const entry = entryId ? entries[entryId] : undefined;
    return entry?.type === EntryType.Graph && entry.graphId ? g.graphs[entry.graphId] : undefined;
  });

  /** Applies or undoes the change; when that doesn't work, the card says so. */
  const attempt = (step: () => void) => {
    try {
      step();
      setFailed(false);
    } catch (error) {
      console.error('[Chat] change failed', error);
      setFailed(true);
    }
  };

  return (
    <Paper withBorder radius="md" p="xs" mt={6}>
      <Text fz="sm" fw={600}>
        {change.field}
      </Text>
      <ChangeValue
        change={change}
        resolved={resolved}
        applied={applied}
        blocks={blocks}
        currentGraph={currentGraph}
      />
      <ChangePreview
        change={change}
        resolved={resolved}
        applied={Boolean(applied)}
        blocks={blocks}
        currentGraph={currentGraph}
      />
      {!applied && !problem && switchesFrom && targetType && (
        <Text fz="xs" c="dimmed" mt={4}>
          {t('chat.change.switches', {
            from: t(`chat.change.type.${switchesFrom}`),
            to: t(`chat.change.type.${targetType}`),
          })}
        </Text>
      )}
      {!applied && !problem && resolved.replacesGraph && (
        <Text fz="xs" c="dimmed" mt={4}>
          {t('chat.change.replacesGraph')}
        </Text>
      )}
      {problem && !applied && (
        <Text fz="xs" c="orange" mt={4}>
          {problemText(change, resolved, blocks)}
        </Text>
      )}
      {failed && (
        <Text fz="xs" c="red" mt={4}>
          {t('chat.change.failed')}
        </Text>
      )}
      {applied ? (
        <Group position="apart" mt={6}>
          <Group spacing={4}>
            <FiCheck size={12} color="var(--mantine-color-green-6, #40c057)" />
            <Text fz="xs" c="green" fw={600}>
              {t('chat.change.applied')}
            </Text>
          </Group>
          <Button
            size="xs"
            variant="subtle"
            onClick={() =>
              attempt(() => {
                undoChange(useStore.getState(), applied);
                setApplied(null);
              })
            }
          >
            {t('chat.change.undo')}
          </Button>
        </Group>
      ) : (
        !problem &&
        entryId && (
          <Group position="right" mt={6}>
            <Button
              size="xs"
              onClick={() =>
                attempt(() =>
                  setApplied(applyChange(useStore.getState(), entryId, change, resolved))
                )
              }
            >
              {t('chat.change.apply')}
            </Button>
          </Group>
        )
      )}
    </Paper>
  );
};
